import React from 'react';
import { Field, Note, Select, T } from './ui';

/**
 * Which channel a campaign is aimed at.
 *
 * A select, not a free-text id: the thing being chosen has an id nobody can type and a name
 * everybody knows.
 *
 * The list is the server's own: every campaign-eligible channel, ordered by name.
 *
 * It used to be the twenty most followed, with a note underneath saying so. That was the defect QA
 * reported as missing team channels — athletes and clubs competed for the same twenty places, and
 * with no search box a club outside them could not be reached by any route. Telling an operator
 * the list was partial did not help: there was nothing they could do about it.
 *
 * ## The saved channel that is no longer in the list
 *
 * A campaign saved against a channel that has since been archived or suspended must still show
 * which channel it named — that is what makes it reviewable — while not being sendable (FR-011).
 * So a stored selection outside the returned list is rendered as its own option, marked, rather
 * than silently dropped. Dropping it would make the control read as though the operator had never
 * chosen anything, and saving would then write that loss back to the campaign.
 */
const ChannelPicker = ({
  channels = [],
  channelId,
  channelLabel,
  loading,
  onSelect,
  unavailable,
  // Passed by the Campaign Builder: this is the campaign's audience, so it is a required field.
  selectId,
  required,
  error
}) => {
  const inList = channels.some((channel) => channel.id === channelId);

  const optionLabel = (channel) =>
    channel.ownerLabel ? `${channel.label} (${channel.ownerLabel})` : channel.label;

  return (
    <>
      <Field label="Channel" required={required} error={error}>
        {loading && !channels.length ? (
          <Note>Loading channels…</Note>
        ) : !channels.length && !channelId ? (
          /*
            An empty list is not an empty control — the same reasoning as the challenge picker.

            A bare empty dropdown says nothing about why it is empty, and with no search box there
            is only one thing it can mean.
          */
          <Note style={{ color: T.amber }}>
            No campaign-eligible channels were found. A channel audience cannot be used until one
            exists.
          </Note>
        ) : (
          <Select
            id={selectId}
            value={channelId || ''}
            onChange={(event) => {
              const next = channels.find((channel) => channel.id === event.target.value);
              onSelect(next || null);
            }}
          >
            <option value="">Choose a channel</option>

            {/*
              The saved channel, kept selectable-looking even when it is outside the list.

              A channel that has since been archived or suspended is not returned by the search,
              so the `<select>` would fall back to the empty option and the operator would watch
              their choice vanish — and saving would write that loss back to the campaign.
            */}
            {channelId && !inList ? (
              <option value={channelId}>
                {unavailable
                  ? `${channelLabel || 'Saved channel'} — no longer available`
                  : channelLabel || 'Saved channel'}
              </option>
            ) : null}

            {channels.map((channel) => (
              <option key={channel.id} value={channel.id}>
                {optionLabel(channel)}
              </option>
            ))}
          </Select>
        )}
      </Field>
    </>
  );
};

export default ChannelPicker;
